/**
 * A `userErrors` entry, in the reader's language.
 *
 * The same contract the product reads: the API sends `{ code, message, params }`
 * and does not localize, so the code is the key and the params are the values.
 * The dictionary is shared, so an administrator and the tutor they are helping
 * read the same sentence for the same refusal — which is the point of having
 * one dictionary rather than an admin one.
 *
 * Duplicated from `apps/web` rather than imported: the two applications do not
 * import each other's source, and a shared package for eleven lines that
 * depend on `@athena/ui`'s React context would be a package for the sake of a
 * rule.
 */
import { DICTIONARIES, lookup, type TranslationKey } from '@athena/i18n'
import { useLanguage, useTranslate } from '@athena/ui'

export interface ApiUserError {
  code: string
  message: string
  params: readonly string[]
}

export function useFirstUserErrorText(): (
  errors: readonly ApiUserError[] | undefined,
  fallback: string,
) => string {
  const t = useTranslate()
  const language = useLanguage()

  return (errors, fallback) => {
    const first = errors?.[0]
    if (!first) return fallback

    const key = `userError.${first.code}`
    if (lookup(DICTIONARIES[language], key) === undefined) {
      // Loud, for the same reason a missing translation key is loud: a code
      // rendering in English on a Spanish page is a defect.
      console.error(`[i18n] no userError entry for code ${first.code}`)
      return t('ui.failure.invalid')
    }
    return t(key as TranslationKey, [...first.params])
  }
}
